import { useState, useCallback } from 'react';
import { HelpCircle, X, BarChart3, FileText, Shield, Activity } from 'lucide-react';
import { AvailabilityTab } from './AvailabilityTab.tsx';
import { MaintenancesTab } from './MaintenancesTab.tsx';
import { AuditTab } from './AuditTab.tsx';
import { ProbeSummaryTab } from './ProbeSummaryTab.tsx';
import { useAvailableMonths } from '../hooks/useReports.ts';

type TabKey = 'availability' | 'maintenances' | 'audit' | 'probe-summary';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'availability', label: 'Disponibilidade' },
  { key: 'maintenances', label: 'Manutenções' },
  { key: 'audit', label: 'Auditoria' },
  { key: 'probe-summary', label: 'Probe Resumido' },
];

/* ── Modal: Como funcionam os relatórios ──────────────── */

function ReportsHelpModal({ onClose }: { onClose: () => void }) {
  const handleBackdrop = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={handleBackdrop}
    >
      <div className="relative mx-4 w-full max-w-lg rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400">
              <HelpCircle size={18} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Sobre os Relatórios</h3>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-5 px-6 py-5">
          {/* R-001 Disponibilidade */}
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800/50 dark:bg-blue-900/20">
            <div className="mb-2 flex items-center gap-2">
              <BarChart3 size={16} className="text-blue-600 dark:text-blue-400" />
              <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-300">Disponibilidade (R-001)</h4>
            </div>
            <div className="space-y-1.5 text-xs text-blue-700 dark:text-blue-300/90">
              <p>Uptime% por tenant em período mensal ou customizado. Inclui minutos fora do ar, incidentes e faixa de desconto SLA.</p>
              <p>Gerado <strong>automaticamente</strong> todo dia 1 para o mês anterior.</p>
            </div>
          </div>

          {/* R-002 Manutenções */}
          <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800/50 dark:bg-green-900/20">
            <div className="mb-2 flex items-center gap-2">
              <FileText size={16} className="text-green-600 dark:text-green-400" />
              <h4 className="text-sm font-semibold text-green-800 dark:text-green-300">Manutenções (R-002)</h4>
            </div>
            <p className="text-xs text-green-700 dark:text-green-300/90">
              Histórico de manutenções com tenants afetados, tipo (agendada/retroativa), duração real e responsável. Filtrável por período e tenant.
            </p>
          </div>

          {/* R-003 Auditoria */}
          <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 dark:border-orange-800/50 dark:bg-orange-900/20">
            <div className="mb-2 flex items-center gap-2">
              <Shield size={16} className="text-orange-600 dark:text-orange-400" />
              <h4 className="text-sm font-semibold text-orange-800 dark:text-orange-300">Auditoria (R-003)</h4>
            </div>
            <div className="space-y-1.5 text-xs text-orange-700 dark:text-orange-300/90">
              <p>Registro de todas as ações realizadas no painel admin: quem fez, o quê, quando e com quais dados.</p>
              <p className="text-[11px] italic">Exportação apenas em PDF (dado sensível).</p>
            </div>
          </div>

          {/* R-004 Probe Resumido */}
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
            <div className="mb-2 flex items-center gap-2">
              <Activity size={16} className="text-gray-600 dark:text-gray-400" />
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Probe Resumido (R-004)</h4>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Visão agregada por tenant: total de checks, % UP/DOWN, erros mais frequentes, pico e média de latência. Diferente do Probe Logs que mostra cada registro individual.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-3 dark:border-gray-700">
          <p className="mb-3 text-center text-[11px] text-gray-500 dark:text-gray-400">
            Todos os relatórios suportam exportação em PDF e CSV (exceto Auditoria: apenas PDF).
          </p>
          <button
            onClick={onClose}
            className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Entendi
          </button>
        </div>
      </div>
    </div>
  );
}

export function ReportsPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('availability');
  const [showHelp, setShowHelp] = useState(false);
  const { data: availableMonths } = useAvailableMonths();

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Relatórios</h1>
        <p className="mt-0.5 flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
          Disponibilidade, manutenções, auditoria e resumo de probes
          <button
            onClick={() => setShowHelp(true)}
            className="rounded-full p-0.5 text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/30 dark:hover:text-blue-400"
            title="Sobre os relatórios"
          >
            <HelpCircle size={16} />
          </button>
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1 dark:border-gray-700 dark:bg-gray-800">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'availability' && <AvailabilityTab availableMonths={availableMonths} />}
      {activeTab === 'maintenances' && <MaintenancesTab availableMonths={availableMonths} />}
      {activeTab === 'audit' && <AuditTab availableMonths={availableMonths} />}
      {activeTab === 'probe-summary' && <ProbeSummaryTab availableMonths={availableMonths} />}

      {/* Modal de ajuda */}
      {showHelp && <ReportsHelpModal onClose={() => setShowHelp(false)} />}
    </div>
  );
}
